import React, { useEffect, useId, useState } from 'react';

import clsx from 'clsx';
import IconArrowDown from '@/assets/icons/icon-arrow-down-01.svg?react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import IconSettings from '@/assets/icons/icon-settings.svg?react';
import { SecondaryButton } from '@/components/buttons/SecondaryButton/SecondaryButton';
import IconCancel from '@/assets/icons/icon-cancel.svg?react';
import { IconSimpleButton } from '@/components/buttons/IconSimpleButton/IconSimpleButton';
import { StylesInspectorLink } from '../StylesInspectorLink/StylesInspectorLink';
import { StylesHeadings } from '../StylesHeadings/StylesHeadings';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import StylesFontFamilySettings from '@/components/dropdowns/SelectField/StylesFontFamilySettings';
import type {
  StyleTypography,
  HeadingLevel,
  BodyTypography,
  LinkStyle,
} from '@builder4/shared/types/styles';
import { StylesBody } from '../StylesBody/StylesBody';

interface StylesTextStyleProps {
  className: string;
  onClick?: () => void;
  onDataChange: (data: StyleTypography) => void;
  isOpenCollapse: boolean;
  value: StyleTypography;
}

// Format link key to display label (e.g., "link1" → "Link 1")
const getLinkLabel = (linkKey: string): string => {
  const match = linkKey.split('link')[1];
  if (match) {
    return `Link ${match}`;
  }
  return linkKey;
};

export const StylesTextStyle: React.FC<StylesTextStyleProps> = ({
  className = '',
  onClick,
  onDataChange,
  value: textStyleData,
  isOpenCollapse,
}) => {
  const instanceId = useId();
  const [linkMenuOpen, setLinkMenuOpen] = useState<Record<number, boolean>>({});

  const handleClick = () => {
    if (onClick) onClick();
  };

  const handleOnChangeHeadingFontFamily = (fontFamily: string) => {
    const next: StyleTypography = {
      ...textStyleData,
      heading: { ...textStyleData.heading, fontFamily },
    };
    onDataChange(next);
  };

  const handleOnChangeBodyFontFamily = (fontFamily: string) => {
    const next: StyleTypography = {
      ...textStyleData,
      body: { ...textStyleData.body, fontFamily },
    };
    onDataChange(next);
  };

  // Update handler for headings
  const handleHeadingUpdate = (
    headingType: 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6',
    data: HeadingLevel,
  ) => {
    const cleanedData = { ...data };
    // You cannot clear the font color in the UI
    if (cleanedData.fontColor === undefined || cleanedData.fontColor === null) {
      cleanedData.fontColor = textStyleData.heading[headingType].fontColor;
    }
    const next: StyleTypography = {
      ...textStyleData,
      heading: {
        ...textStyleData.heading,
        [headingType]: { ...textStyleData.heading[headingType], ...cleanedData },
      },
    };
    onDataChange(next);
  };

  // Update handler for body variants
  const handleBodyUpdate = (_bodyVariant: 'large' | 'medium' | 'small', data: BodyTypography) => {
    const next: StyleTypography = {
      ...textStyleData,
      body: data,
    };
    onDataChange(next);
  };

  // Update handler for links
  const handleLinkUpdate = (linkKey: 'link1' | 'link2', data: Partial<LinkStyle>) => {
    const fontColor =
      data.fontColor !== undefined
        ? typeof data.fontColor === 'string'
          ? data.fontColor
          : data.fontColor
        : textStyleData.link[linkKey].fontColor;
    const backgroundColor =
      data.backgroundColor !== undefined
        ? data.backgroundColor
        : textStyleData.link[linkKey].backgroundColor;
    const next: StyleTypography = {
      ...textStyleData,
      link: {
        ...textStyleData.link,
        [linkKey]: {
          ...textStyleData.link[linkKey],
          ...(fontColor !== undefined && { fontColor }),
          ...(backgroundColor !== undefined && { backgroundColor }),
          ...(data.fontWeight !== undefined && { fontWeight: data.fontWeight }),
          ...(data.decoration !== undefined && { decoration: data.decoration }),
        },
      },
    };
    onDataChange(next);
  };

  // Close any open link menus when another styles dropdown (anywhere) opens
  useEffect(() => {
    const onOtherOpen = (e: Event) => {
      const ev = e as CustomEvent<{ id?: string }>;
      const openedId = String(ev.detail?.id ?? '');
      // If the opener is not from this component instance, close all link menus here
      if (!openedId.startsWith(String(instanceId))) {
        setLinkMenuOpen({});
      }
    };
    window.addEventListener('lp:styles-edit-open', onOtherOpen as EventListener);
    return () => window.removeEventListener('lp:styles-edit-open', onOtherOpen as EventListener);
  }, [instanceId]);

  return (
    <Collapsible defaultOpen={isOpenCollapse} className={clsx(className)}>
      <CollapsibleTrigger
        onClick={handleClick}
        className={clsx(
          'group bg-neutral-0 w-full cursor-pointer border-t border-neutral-100 px-2 py-4',
        )}
      >
        <div className="flex w-full items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <IconSettings className="h-4 w-4 text-neutral-900" />
            <span className="text-button-sm text-neutral-900">Text Styles</span>
          </div>
          <IconArrowDown className="h-4 w-4 text-neutral-500 transition-all group-data-[state=open]:rotate-180" />
        </div>
      </CollapsibleTrigger>

      <CollapsibleContent className={clsx('bg-neutral-0 w-full py-3')}>
        {/* Headings section */}
        <div className="flex flex-col gap-3 pb-3">
          <div className="px-2 pb-1">
            <p className="text-xs font-semibold text-neutral-900">Headings</p>
          </div>

          <div className="px-2">
            <StylesFontFamilySettings
              value={textStyleData.heading.fontFamily}
              onChange={handleOnChangeHeadingFontFamily}
              className="w-full"
            />
          </div>

          {/* Rows H1–H6 */}
          <div className="flex flex-col gap-4">
            <div className="px-2">
              <StylesHeadings
                className="w-52"
                label="Heading H1"
                fontSizeLabel={textStyleData.fontSizeUnit}
                headingType="h1"
                currentData={textStyleData.heading.h1}
                onUpdate={(data) => handleHeadingUpdate('h1', data)}
              />
            </div>
            <div className="px-2">
              <StylesHeadings
                className="w-52"
                label="Heading H2"
                fontSizeLabel={textStyleData.fontSizeUnit}
                headingType="h2"
                currentData={textStyleData.heading.h2}
                onUpdate={(data) => handleHeadingUpdate('h2', data)}
              />
            </div>
            <div className="px-2">
              <StylesHeadings
                className="w-52"
                label="Heading H3"
                fontSizeLabel={textStyleData.fontSizeUnit}
                headingType="h3"
                currentData={textStyleData.heading.h3}
                onUpdate={(data) => handleHeadingUpdate('h3', data)}
              />
            </div>
            <div className="px-2">
              <StylesHeadings
                className="w-52"
                label="Heading H4"
                fontSizeLabel={textStyleData.fontSizeUnit}
                headingType="h4"
                currentData={textStyleData.heading.h4}
                onUpdate={(data) => handleHeadingUpdate('h4', data)}
              />
            </div>
            <div className="px-2">
              <StylesHeadings
                className="w-52"
                label="Heading H5"
                fontSizeLabel={textStyleData.fontSizeUnit}
                headingType="h5"
                currentData={textStyleData.heading.h5}
                onUpdate={(data) => handleHeadingUpdate('h5', data)}
              />
            </div>
            <div className="px-2">
              <StylesHeadings
                className="w-52"
                label="Heading H6"
                fontSizeLabel={textStyleData.fontSizeUnit}
                headingType="h6"
                currentData={textStyleData.heading.h6}
                onUpdate={(data) => handleHeadingUpdate('h6', data)}
              />
            </div>
          </div>
        </div>

        {/* Body section */}
        <div className="flex flex-col gap-3">
          <div className="px-2 pb-1">
            <p className="text-xs font-semibold text-neutral-900">Body</p>
          </div>

          <div className="px-2">
            <StylesFontFamilySettings
              value={textStyleData.body.fontFamily || 'Inter'}
              onChange={handleOnChangeBodyFontFamily}
              className="w-full"
            />
          </div>

          {/* Body size rows - using StylesHeadings popovers in body mode */}
          <div className="flex flex-col gap-4">
            <div className="px-2">
              <StylesBody
                className="w-52"
                label="Body Large"
                fontSizeLabel={textStyleData.fontSizeUnit}
                bodyVariant="large"
                currentData={textStyleData.body}
                onUpdate={(data) => handleBodyUpdate('large', data)}
              />
            </div>
            <div className="px-2">
              <StylesBody
                className="w-52"
                label="Body Medium"
                fontSizeLabel={textStyleData.fontSizeUnit}
                bodyVariant="medium"
                currentData={textStyleData.body}
                onUpdate={(data) => handleBodyUpdate('medium', data)}
              />
            </div>
            <div className="px-2">
              <StylesBody
                className="w-52"
                label="Body Small"
                fontSizeLabel={textStyleData.fontSizeUnit}
                bodyVariant="small"
                currentData={textStyleData.body}
                onUpdate={(data) => handleBodyUpdate('small', data)}
              />
            </div>
          </div>
        </div>

        {/* Links section */}
        <div className="mt-3 flex flex-col gap-3">
          <div className="px-2 pb-1">
            <p className="text-xs font-semibold text-neutral-900">Links</p>
          </div>
          {Object.keys(textStyleData.link).map((name, idx) => (
            <div key={idx} className="px-2">
              <div className="flex h-8 w-full items-center justify-between">
                <div className="flex h-8 min-w-0 items-center">
                  <span className="text-button-sm truncate font-medium text-neutral-500">
                    {getLinkLabel(name)}
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <DropdownMenu
                    open={!!linkMenuOpen[idx]}
                    onOpenChange={(v) => {
                      const w = window as unknown as { __lpColorPickerOpen?: number };
                      const pickerOpen = (w.__lpColorPickerOpen ?? 0) > 0;
                      if (!v && pickerOpen) return; // ignore close while ColorPicker is open
                      if (v) {
                        // Announce globally so other styles dropdowns close
                        try {
                          window.dispatchEvent(
                            new CustomEvent('lp:styles-edit-open', {
                              detail: { id: `${instanceId}-link-${idx}` },
                            }),
                          );
                        } catch {
                          /* noop */
                        }
                      }
                      setLinkMenuOpen((s) => {
                        // Open current and close all others to keep only one dropdown open at a time
                        if (v) return { [idx]: true };
                        // When closing this one, just mark it closed and keep others as-is
                        const next = { ...s, [idx]: false } as Record<number, boolean>;
                        return next;
                      });
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <SecondaryButton size="sm" state="default" text="Edit" icon={null} />
                    </DropdownMenuTrigger>
                    <DropdownMenuContent
                      className="bg-neutral-0 relative z-10 inline-flex w-[248px] flex-col items-center justify-start gap-0 rounded-lg p-0 shadow-lg outline outline-neutral-100"
                      side="right"
                      align="center"
                      sideOffset={16}
                      collisionPadding={{ top: 4, bottom: 4 }}
                      onWheelCapture={(e) => e.stopPropagation()}
                    >
                      <div className="bg-neutral-0 sticky top-0 z-10 flex h-10 min-h-10 w-full shrink-0 items-center justify-between border-b border-neutral-100 px-3 py-2">
                        <p className="text-xs leading-tight font-semibold text-neutral-900">
                          {getLinkLabel(name)}
                        </p>
                        <IconSimpleButton
                          icon={IconCancel}
                          size={24}
                          state="default"
                          onClick={() => setLinkMenuOpen((s) => ({ ...s, [idx]: false }))}
                        />
                      </div>
                      <div
                        className="no-scrollbar max-h-[calc(100vh-120px)] w-full overflow-y-auto overscroll-contain"
                        onWheelCapture={(e) => e.stopPropagation()}
                      >
                        <StylesInspectorLink
                          className="min-w-full"
                          currentFontFamily={textStyleData.body.fontFamily}
                          value={textStyleData.link[idx === 0 ? 'link1' : 'link2']}
                          onChange={(data) => handleLinkUpdate(idx === 0 ? 'link1' : 'link2', data)}
                        />
                      </div>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </div>
              </div>
            </div>
          ))}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};
